import { useState, useEffect } from "react";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { validateBetaCode, normalizeBetaCode, grantBetaAccess, isBetaUser } from "@/lib/betaAccess";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface BetaCodeInputProps {
  compact?: boolean;
  onSuccess?: () => void;
}

const BetaCodeInput = ({ compact = false, onSuccess }: BetaCodeInputProps) => {
  const { user, refreshProfile } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(isBetaUser());
  const [error, setError] = useState("");

  // Keep success state in sync if access was granted elsewhere (e.g. /private)
  useEffect(() => {
    if (!success && isBetaUser()) setSuccess(true);
  }, [success]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = normalizeBetaCode(code);
    if (!normalized) return;

    setLoading(true);
    setError("");

    // 1. Always validate locally first (single source of truth, instant feedback)
    if (!validateBetaCode(code)) {
      setError("Invalid access code.");
      setLoading(false);
      return;
    }

    // 2. Grant local access immediately so unauthenticated testers also unlock
    grantBetaAccess();

    // 3. If signed in, also flip the server-side flag so the unlock persists
    //    across devices and is reflected in profiles.beta_access (used by useSubscription)
    if (user) {
      try {
        const { data, error: fnError } = await supabase.functions.invoke("redeem-beta-code", {
          body: { code: normalized },
        });
        // Non-fatal: local access is already granted. Surface server error only if it's
        // not the "already redeemed" case.
        if (fnError || (data && !data.success)) {
          const msg = data?.error || fnError?.message || "";
          if (msg && !/already redeemed/i.test(msg)) {
            console.warn("[beta-code] server redemption warning:", msg);
          }
        }
        await refreshProfile();
      } catch (err) {
        console.warn("[beta-code] server redemption failed (local access still active):", err);
      }
    }

    setSuccess(true);
    toast.success("Beta Access Active — all features unlocked.");
    onSuccess?.();
    setLoading(false);
  };

  if (success) {
    return (
      <div className={`flex flex-col items-center gap-1 ${compact ? "text-xs" : "text-sm"} text-primary font-medium`}>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Beta Access Active
        </div>
        {!compact && (
          <p className="text-xs text-muted-foreground font-normal">
            Friends & Family beta access has been enabled on this device.
          </p>
        )}
      </div>
    );
  }

  if (compact) {
    return (
      <form onSubmit={handleSubmit} className="space-y-1.5">
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <KeyRound className="h-3 w-3" />
          Have a beta access code?
        </div>
        <div className="flex gap-1.5">
          <Input
            type="text"
            placeholder="Enter code"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(""); }}
            className="h-7 text-xs flex-1"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
          />
          <Button type="submit" size="sm" variant="outline" className="h-7 text-xs px-2.5" disabled={loading || !code.trim()}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Redeem"}
          </Button>
        </div>
        {error && <p className="text-[10px] text-destructive">{error}</p>}
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <KeyRound className="h-4 w-4" />
        Have a beta access code?
      </div>
      <div className="flex gap-2 max-w-xs">
        <Input
          type="text"
          placeholder="Enter access code"
          value={code}
          onChange={(e) => { setCode(e.target.value); setError(""); }}
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          className="flex-1"
        />
        <Button type="submit" variant="secondary" disabled={loading || !code.trim()}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </form>
  );
};

export default BetaCodeInput;
